import {
    isValidWellSkyAccountName,
    normalizeWellSkyAccountName,
    WELLSKY_ACCOUNT_DOMAIN_EXAMPLE,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { WellSkyDedicatedUserNote } from "./wellsky-dedicated-user-note";

interface Props {
    initialUsername: string;
    initialAccountName: string;
    onSaved: () => void;
    onCancel: () => void;
    onError: (message: string) => void;
}

export const WellSkyWebCredentialForm: React.FC<Props> = props => {
    const { initialUsername, initialAccountName, onSaved, onCancel, onError } = props;
    const trpcClient = trpc.useTRPC();

    const [username, setUsername] = React.useState(initialUsername);
    const [accountName, setAccountName] = React.useState(initialAccountName);
    const [accountNameTouched, setAccountNameTouched] = React.useState(false);
    const [password, setPassword] = React.useState("");

    const storeMutation = useMutation({
        ...trpcClient.wellSkyPersonalCare.storeCredential.mutationOptions(),
        onSuccess: onSaved,
        onError: error => onError(error.message || "Failed to save credentials"),
    });

    const handleAccountNameBlur = React.useCallback(() => {
        setAccountName(current => normalizeWellSkyAccountName(current));
        setAccountNameTouched(true);
    }, []);

    const handleSave = React.useCallback(() => {
        storeMutation.mutate({
            username,
            accountName: normalizeWellSkyAccountName(accountName),
            password,
        });
    }, [username, accountName, password, storeMutation]);

    const accountNameIsValid = isValidWellSkyAccountName(accountName);
    const showAccountNameError =
        accountNameTouched && accountName.trim() !== "" && !accountNameIsValid;

    return (
        <div className="space-y-4">
            <div>
                <Input
                    label="Account domain"
                    value={accountName}
                    onChange={e => setAccountName(e.target.value)}
                    onBlur={handleAccountNameBlur}
                    placeholder={WELLSKY_ACCOUNT_DOMAIN_EXAMPLE}
                    aria-invalid={showAccountNameError}
                />
                {showAccountNameError && (
                    <p className="mt-1 text-sm text-red-600">
                        Enter a clearcareonline.com domain, such as{" "}
                        {WELLSKY_ACCOUNT_DOMAIN_EXAMPLE}.
                    </p>
                )}
            </div>
            <WellSkyDedicatedUserNote />
            <Input
                label="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="username@yourcompany.com"
            />
            <Input
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter the password"
            />
            <div className="flex justify-end space-x-2">
                <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                <PrimaryButton
                    onClick={handleSave}
                    disabled={
                        !username ||
                        !accountNameIsValid ||
                        !password ||
                        storeMutation.isPending
                    }>
                    {storeMutation.isPending && <Spinner />}
                    <span className={storeMutation.isPending ? "ml-2" : ""}>
                        {storeMutation.isPending ? "Verifying..." : "Save"}
                    </span>
                </PrimaryButton>
            </div>
        </div>
    );
};
